// Floating viewer panels and action buttons must use these shared token-backed styles.
// Do not invent one-off panel glass/background/border/shadow or CTA values in components.
export const viewerPanelSurfaceSx = {
  background:
    "var(--surface-gradient-dark, linear-gradient(160deg, rgba(10, 16, 24, 0.88) 0%, rgba(12, 18, 28, 0.8) 100%))",
  borderRadius: "var(--radius-lg, 14px)",
  boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
  backdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
  WebkitBackdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
};

export const getViewerPanelPaddingSx = (isPhone = false) => ({
  px: isPhone
    ? "var(--viewer-panel-padding-x-phone, 14px)"
    : "var(--viewer-panel-padding-x, 17px)",
  pt: isPhone
    ? "var(--viewer-panel-padding-top-phone, 14px)"
    : "var(--viewer-panel-padding-top, 17px)",
  pb: isPhone
    ? "var(--viewer-panel-padding-y-phone, 10px)"
    : "var(--viewer-panel-padding-y, 11px)",
});

// Shared panel typography and layout. Viewer panels should compose these
// primitives instead of defining their own eyebrow, title, or body values.
export const viewerPanelHeaderSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1,
};

export const viewerPanelHeadingGroupSx = {
  minWidth: 0,
  flex: "1 1 auto",
  textAlign: "left",
};

export const viewerPanelEyebrowSx = {
  display: "block",
  color: "rgba(161, 241, 224, 0.9)",
  opacity: 1,
  fontSize: "10px",
  fontWeight: 400,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
};

export const viewerPanelTitleSx = {
  m: 0,
  mt: 0.35,
  overflow: "hidden",
  color: "var(--label-text, #f2fffc)",
  fontSize: "1.05rem",
  fontWeight: 650,
  lineHeight: 1.25,
  textAlign: "left",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const viewerPanelBodySx = {
  color: "rgba(242, 255, 252, 0.82)",
  fontSize: "0.8rem",
  lineHeight: 1.48,
};

export const viewerPanelSectionHeadingSx = {
  color: "var(--label-text, #f2fffc)",
  fontSize: "0.95rem",
  fontWeight: 700,
  lineHeight: 1.25,
  textAlign: "left",
};

export const getViewerLabelPaddingSx = (hasSubtitle = false) => ({
  px: "var(--viewer-label-padding-x, 17px)",
  py: "var(--viewer-label-padding-y, 11px)",
  gap: hasSubtitle ? "var(--viewer-label-gap, 5px)" : 0,
});

export const getViewerActionPaddingX = (isPhone = false) =>
  isPhone
    ? "var(--viewer-action-padding-x-phone, 14px)"
    : "var(--viewer-action-padding-x, 17px)";

export const viewerCompactActionPaddingX =
  "var(--viewer-action-padding-x-compact, 13px)";

export const viewerActionButtonIconSx = {
  fontSize: "18px",
};

const viewerActionButtonHoverSx = {
  background:
    "var(--recording-cta-gradient-hover, linear-gradient(135deg, rgba(161, 248, 241, 1) 0%, rgba(109, 234, 224, 1) 100%))",
  borderColor: "rgba(210, 255, 249, 0.42)",
  boxShadow:
    "0 0 20px rgba(89, 218, 230, 0.24), var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
  color: "var(--recording-cta-text, #083033)",
  transform: "translateY(-1px)",
};

export const viewerActionButtonSx = {
  minHeight: "var(--viewer-action-min-height, 38px)",
  minWidth: 0,
  padding: 0,
  px: getViewerActionPaddingX(false),
  py: "var(--viewer-action-padding-y, 11px)",
  borderRadius: "12px",
  boxSizing: "border-box",
  border: "1px solid rgba(210, 255, 249, 0.28)",
  background:
    "var(--recording-cta-gradient, linear-gradient(135deg, rgba(133, 245, 236, 1) 0%, rgba(90, 222, 212, 1) 100%))",
  boxShadow:
    "var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
  backdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
  WebkitBackdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
  color: "var(--recording-cta-text, #083033)",
  textDecoration: "none",
  textTransform: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  fontFamily:
    "var(--control-font-family, \"area-normal\", Arial, Helvetica, sans-serif)",
  fontSize: "var(--viewer-action-font-size, 14px)",
  fontStyle: "normal",
  fontWeight: "var(--viewer-action-font-weight, 700)",
  fontSynthesis: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  lineHeight: "var(--viewer-action-line-height, 1)",
  whiteSpace: "nowrap",
  cursor: "pointer",
  pointerEvents: "auto",
  transition:
    "transform 160ms ease, box-shadow 160ms ease, background 160ms ease, border-color 160ms ease, color 160ms ease",
  "&:hover": viewerActionButtonHoverSx,
  "&:focus-visible": {
    ...viewerActionButtonHoverSx,
    outline: "2px solid rgba(161, 241, 224, 0.82)",
    outlineOffset: 3,
  },
  "& .control-label": {
    display: "block",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "inherit",
    textBoxTrim: "trim-both",
    textBoxEdge: "cap alphabetic",
    textBox: "trim-both cap alphabetic",
    transform: "translateY(var(--control-label-y-offset, 0px))",
  },
  "& .MuiSvgIcon-root, & svg": {
    display: "block",
    flexShrink: 0,
    ...viewerActionButtonIconSx,
    lineHeight: 1,
  },
  "&:active": {
    boxShadow:
      "0 2px 5px rgba(0, 48, 58, 0.32), inset 0 1px 2px rgba(0, 0, 0, 0.22)",
    transform: "translateY(1px)",
  },
  "&:disabled, &[aria-disabled='true']": {
    background: "var(--recording-cta-disabled, rgba(133, 245, 236, 0.35))",
    boxShadow: "none",
    color: "var(--recording-cta-text-disabled, rgba(6, 25, 27, 0.5))",
    cursor: "not-allowed",
    transform: "none",
  },
  "&::-moz-focus-inner": {
    border: 0,
    padding: 0,
  },
};

export const viewerPillActionButtonSx = {
  ...viewerActionButtonSx,
  borderRadius: "999px",
};

export const viewerSecondaryActionButtonSx = {
  ...viewerPillActionButtonSx,
  background: "rgba(0, 0, 0, 0.48)",
  color: "var(--label-subtitle, rgba(210, 255, 249, 0.78))",
  boxShadow: "none",
  borderColor: "rgba(133, 245, 236, 0.9)",
  "&:hover": {
    transform: "translateY(-1px)",
    background: "rgba(8, 28, 34, 0.68)",
    borderColor: "rgba(161, 248, 241, 0.95)",
    boxShadow: "0 0 18px rgba(89, 218, 230, 0.18)",
  },
  "&:focus-visible": {
    outline: "2px solid rgba(161, 241, 224, 0.82)",
    outlineOffset: 3,
  },
};

export const viewerGhostActionButtonSx = {
  ...viewerActionButtonSx,
  px: "var(--viewer-action-padding-x-compact, 13px)",
  py: "var(--viewer-action-padding-y, 11px)",
  border: 0,
  borderRadius: "999px",
  background: "rgba(0, 0, 0, 0.28)",
  color: "var(--label-subtitle, rgba(210, 255, 249, 0.78))",
  boxShadow: "none",
  gap: 0.7,
  "&:hover": {
    background: "rgba(8, 28, 34, 0.62)",
    color: "#a1f8f1",
    boxShadow: "none",
    transform: "translateY(-1px)",
  },
  "&:focus-visible": {
    outline: "2px solid rgba(161, 241, 224, 0.82)",
    outlineOffset: 3,
  },
};

export const viewerSubtleIconButtonSx = {
  ...viewerGhostActionButtonSx,
  width: 30,
  height: 30,
  minWidth: 30,
  minHeight: 30,
  p: 0,
  px: 0,
  py: 0,
  border: "1px solid rgba(210, 255, 249, 0.1)",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.035)",
  color: "rgba(210, 255, 249, 0.58)",
  boxShadow: "none",
  opacity: 0.82,
  transform: "none",
  "&:hover": {
    background: "rgba(210, 255, 249, 0.08)",
    borderColor: "rgba(210, 255, 249, 0.18)",
    color: "rgba(242, 255, 252, 0.82)",
    boxShadow: "none",
    opacity: 1,
    transform: "none",
  },
  "&:focus-visible": {
    background: "rgba(210, 255, 249, 0.08)",
    borderColor: "rgba(210, 255, 249, 0.24)",
    color: "rgba(242, 255, 252, 0.88)",
    outline: "2px solid rgba(161, 241, 224, 0.48)",
    outlineOffset: 3,
    boxShadow: "none",
  },
  "&:active": {
    background: "rgba(210, 255, 249, 0.11)",
    boxShadow: "none",
    transform: "none",
  },
};

export const viewerActionButtonLabelSx = {
  color: "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily:
    "var(--control-font-family, \"area-normal\", Arial, Helvetica, sans-serif)",
  fontSize: "14px",
  fontWeight: "var(--viewer-action-font-weight, 700)",
  fontSynthesis: "none",
  letterSpacing: "0.01em",
  lineHeight: "var(--viewer-action-line-height, 1)",
  textBoxTrim: "trim-both",
  textBoxEdge: "cap alphabetic",
  textBox: "trim-both cap alphabetic",
  transform: "translateY(var(--control-label-y-offset, 0px))",
};
